/**
 * Aramgah · connect-section
 *
 * Where the sleep diary stops being self-reported and starts pulling from
 * every wearable + health surface. Six connectors: HealthKit (native shell),
 * Google Fit, Fitbit, Oura, Garmin (CSV), Web Bluetooth (live BLE ticks).
 *
 * Wearable data feeds the existing sleep-stage classifier + 90-day chronotype
 * regression. Daily payloads are summarised + persisted via OPFS; raw bytes
 * stay on-device.
 */
import {
  HealthKitConnector,
  GoogleFitConnector,
  FitbitConnector,
  OuraConnector,
  GarminStubConnector,
  WebBluetoothConnector,
  PURPOSE_LABEL,
  PHI_SUBCATEGORY,
  RETENTION_DAYS,
  type ConnectorStatus,
  type HealthPurpose,
  type BleHeartTick,
} from '../connectors/health/index.ts';
import { grant, withdraw, hasConsent } from '../chassis/consent.ts';

interface ConnectorRow {
  readonly purpose: HealthPurpose;
  readonly consentScope: string;
  readonly title: string;
  readonly subtitleHi: string;
  readonly adapter: { id: string; getStatus(): Promise<ConnectorStatus>; connect(): Promise<unknown>; disconnect(): Promise<void>; importPayload(): Promise<unknown> };
  readonly accepts: 'oauth' | 'csv' | 'ble' | 'native';
}

const PRINCIPAL_ID = 'aramgah-default-principal';

const ROWS: readonly ConnectorRow[] = [
  { purpose: 'connector-healthkit-read', consentScope: 'healthkit_read', title: 'Apple HealthKit', subtitleHi: 'iPhone shell · sleep + heart + HRV + steps.', adapter: new HealthKitConnector(PRINCIPAL_ID), accepts: 'native' },
  { purpose: 'connector-googlefit-read', consentScope: 'healthkit_read', title: 'Google Fit', subtitleHi: 'Android shell · 30-day sleep + activity.', adapter: new GoogleFitConnector(PRINCIPAL_ID), accepts: 'oauth' },
  { purpose: 'connector-fitbit-read', consentScope: 'healthkit_read', title: 'Fitbit', subtitleHi: 'Daily ring · steps + sleep efficiency + RHR.', adapter: new FitbitConnector(PRINCIPAL_ID), accepts: 'oauth' },
  { purpose: 'connector-oura-read', consentScope: 'healthkit_read', title: 'Oura Ring', subtitleHi: 'Readiness score + REM + body temp delta.', adapter: new OuraConnector(PRINCIPAL_ID), accepts: 'oauth' },
  { purpose: 'connector-garmin-upload', consentScope: 'healthkit_read', title: 'Garmin Connect (CSV)', subtitleHi: 'Export CSV from Garmin web; we parse locally.', adapter: new GarminStubConnector(PRINCIPAL_ID), accepts: 'csv' },
  { purpose: 'connector-web-bluetooth', consentScope: 'camera_ppg', title: 'Web Bluetooth wearable', subtitleHi: 'Mi Band 8/9, BoAt smartwatch, BLE heart-rate.', adapter: new WebBluetoothConnector(PRINCIPAL_ID), accepts: 'ble' },
];

const TPL = /* html */ `
<style>
  :host { display: block; margin: 28px 0; }
  .card {
    border: 1px solid oklch(85% 0.02 248 / 0.14);
    border-radius: 14px;
    padding: 24px 26px;
    background: linear-gradient(180deg, oklch(11% 0.04 270 / 0.6), oklch(8% 0.03 268 / 0.7));
    box-shadow: 0 18px 40px -22px oklch(0% 0 0 / 0.85);
  }
  .eyebrow {
    font-family: var(--mono);
    font-size: 11px;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: oklch(70% 0.04 60);
    margin-bottom: 10px;
  }
  h3 {
    margin: 0 0 6px;
    font-family: var(--display);
    font-weight: 500;
    font-size: clamp(20px, 4vw, 26px);
    color: oklch(94% 0.04 88);
  }
  .sub {
    font-family: var(--display);
    font-style: italic;
    color: oklch(72% 0.04 250);
    margin-bottom: 18px;
    font-size: 13px;
  }
  ul { list-style: none; padding: 0; margin: 0; }
  li.row {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 14px;
    align-items: start;
    padding: 14px 0;
    border-bottom: 1px dashed oklch(85% 0.02 248 / 0.12);
  }
  li.row:last-child { border-bottom: 0; }
  .name {
    font-family: var(--display);
    font-size: 15px;
    font-weight: 500;
    color: oklch(94% 0.04 88);
    margin-bottom: 2px;
  }
  .hi {
    font-family: var(--display);
    font-style: italic;
    color: oklch(72% 0.04 250);
    font-size: 12.5px;
    margin-bottom: 6px;
  }
  .meta {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    align-items: center;
  }
  .phi {
    font-family: var(--mono);
    font-size: 9px;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    padding: 2px 8px;
    border-radius: 999px;
    background: oklch(28% 0.08 25);
    color: oklch(78% 0.18 25);
  }
  .phi.medium { background: oklch(28% 0.08 70); color: oklch(80% 0.16 70); }
  .phi.low { background: oklch(28% 0.06 200); color: oklch(80% 0.14 200); }
  .retain {
    font-family: var(--mono);
    font-size: 9px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: oklch(72% 0.04 250);
  }
  .status-pill {
    font-family: var(--mono);
    font-size: 9px;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    padding: 2px 8px;
    border-radius: 999px;
    background: oklch(28% 0.08 25);
    color: oklch(80% 0.18 25);
  }
  .status-pill.live {
    background: oklch(28% 0.08 150);
    color: oklch(82% 0.16 150);
  }
  .actions {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 140px;
  }
  button {
    background: oklch(72% 0.16 60);
    color: oklch(10% 0.04 270);
    border: 0;
    padding: 7px 14px;
    border-radius: 999px;
    font-family: var(--mono);
    font-size: 9.5px;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    cursor: pointer;
    font-weight: 600;
  }
  button.ghost {
    background: transparent;
    color: oklch(72% 0.04 250);
    border: 1px solid oklch(72% 0.04 250 / 0.32);
    font-weight: 500;
  }
  button.danger {
    background: transparent;
    color: oklch(78% 0.18 25);
    border: 1px solid oklch(78% 0.18 25 / 0.4);
    font-weight: 500;
  }
  input[type=file] {
    width: 100%;
    color: oklch(85% 0.04 250);
    font-family: var(--mono);
    font-size: 12px;
  }
  .drawer { margin-top: 8px; display: none; }
  .row.expanded .drawer { display: block; }
  .live-feed {
    margin-top: 10px;
    padding: 10px 12px;
    border-radius: 10px;
    background: oklch(15% 0.04 268);
    font-family: var(--mono);
    font-size: 12px;
    color: oklch(82% 0.06 150);
  }
  .bpm { font-size: 22px; font-weight: 600; color: oklch(78% 0.18 25); }
  .total {
    margin-top: 18px;
    padding-top: 14px;
    border-top: 1px solid oklch(85% 0.02 248 / 0.16);
    display: flex;
    justify-content: space-between;
    font-family: var(--mono);
    font-size: 10px;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: oklch(72% 0.04 250);
  }
</style>
<section class="card">
  <div class="eyebrow">Connections · sleep + vitals</div>
  <h3>Raat ka data, har wearable se</h3>
  <p class="sub">Six wearable + native-shell ingress paths. HRV, sleep stages, RHR, body-temp delta all flow back into the chronotype regression. Nothing leaves your device.</p>
  <ul id="rows"></ul>
  <div class="total">
    <span>Connected: <b id="connected-count">0</b> / 6</span>
    <span>Days ingested: <b id="days-count">0</b></span>
  </div>
</section>
`;

interface RowState {
  status: ConnectorStatus | null;
  days: number;
  expanded: boolean;
  liveBpm: number | null;
}

class AramgahConnectSection extends HTMLElement {
  private rowState = new Map<string, RowState>();
  private bleUnsubscribe: (() => void) | null = null;

  connectedCallback(): void {
    this.attachShadow({ mode: 'open' });
    this.shadowRoot!.innerHTML = TPL;
    this.render();
    void this.refreshStatuses();
  }

  disconnectedCallback(): void {
    this.bleUnsubscribe?.();
    this.bleUnsubscribe = null;
  }

  private render(): void {
    const ul = this.shadowRoot!.getElementById('rows');
    if (!ul) return;
    ul.innerHTML = '';
    for (const row of ROWS) {
      const li = document.createElement('li');
      li.className = 'row';
      li.dataset.id = row.adapter.id;
      const state = this.rowState.get(row.adapter.id);
      const connected = state?.status?.connected ?? false;
      const phi = PHI_SUBCATEGORY[row.purpose];
      const phiClass = phi === 'PHI-high' ? '' : phi === 'PHI-medium' ? 'medium' : 'low';
      const retentionLabel = `${RETENTION_DAYS[row.purpose]}d retention`;
      li.innerHTML = `
        <div>
          <div class="name">${row.title}</div>
          <div class="hi">${row.subtitleHi}</div>
          <div class="meta">
            <span class="phi ${phiClass}">${phi}</span>
            <span class="retain">${retentionLabel}</span>
            <span class="status-pill ${connected ? 'live' : ''}">${connected ? 'connected' : 'disconnected'}</span>
          </div>
          ${this.renderDrawer(row, state)}
        </div>
        <div class="actions">
          <button data-act="connect">${connected ? 'Refresh' : (row.accepts === 'ble' ? 'Pair BLE' : 'Connect')}</button>
          ${connected ? '<button class="danger" data-act="disconnect">Disconnect</button>' : ''}
          ${row.accepts === 'csv' ? '<button class="ghost" data-act="toggle">Upload CSV</button>' : ''}
        </div>
      `;
      li.querySelector('button[data-act="connect"]')?.addEventListener('click', () => void this.onConnect(row));
      li.querySelector('button[data-act="disconnect"]')?.addEventListener('click', () => void this.onDisconnect(row));
      li.querySelector('button[data-act="toggle"]')?.addEventListener('click', () => this.toggleDrawer(row));
      const fileInput = li.querySelector('input[type=file]') as HTMLInputElement | null;
      fileInput?.addEventListener('change', (e) => void this.onCsv(row, e));
      ul.appendChild(li);
    }
    this.refreshCounts();
  }

  private renderDrawer(row: ConnectorRow, state: RowState | undefined): string {
    if (row.accepts === 'csv') {
      return `<div class="drawer" ${state?.expanded ? 'style="display:block"' : ''}><input type="file" accept=".csv,text/csv" /><div style="margin-top:6px;font-family:var(--mono);font-size:10px;color:oklch(72% 0.04 250)">CSV from Garmin Connect web export.</div></div>`;
    }
    if (row.accepts === 'ble' && state?.liveBpm) {
      return `<div class="live-feed">Live · <span class="bpm">${state.liveBpm}</span> bpm</div>`;
    }
    if (row.accepts === 'native') {
      return `<div class="drawer" style="display:block;font-family:var(--mono);font-size:10px;color:oklch(72% 0.04 250);margin-top:6px">Native iOS shell required for live mode. Test mode shows 30 days of sample sleep, HRV, RHR.</div>`;
    }
    return '';
  }

  private async refreshStatuses(): Promise<void> {
    for (const row of ROWS) {
      const s = await row.adapter.getStatus();
      const existing = this.rowState.get(row.adapter.id);
      this.rowState.set(row.adapter.id, {
        status: s,
        days: existing?.days ?? 0,
        expanded: existing?.expanded ?? false,
        liveBpm: existing?.liveBpm ?? null,
      });
    }
    this.render();
  }

  private async onConnect(row: ConnectorRow): Promise<void> {
    const scope = row.consentScope as Parameters<typeof grant>[0];
    if (!hasConsent(scope)) {
      grant(scope, PURPOSE_LABEL[row.purpose]);
    }
    if (row.accepts === 'ble') {
      const ble = row.adapter as WebBluetoothConnector;
      const r = await ble.connect();
      if (r.ok) {
        this.bleUnsubscribe?.();
        this.bleUnsubscribe = ble.subscribe((tick: BleHeartTick) => {
          const existing = this.rowState.get(row.adapter.id);
          this.rowState.set(row.adapter.id, {
            status: existing?.status ?? null,
            days: existing?.days ?? 0,
            expanded: existing?.expanded ?? false,
            liveBpm: tick.bpm,
          });
          this.render();
        });
      }
      const existing = this.rowState.get(row.adapter.id);
      this.rowState.set(row.adapter.id, {
        status: await row.adapter.getStatus(),
        days: existing?.days ?? 0,
        expanded: existing?.expanded ?? false,
        liveBpm: existing?.liveBpm ?? null,
      });
      this.render();
      return;
    }
    await row.adapter.connect();
    const payload = await row.adapter.importPayload() as { ok: boolean; value?: unknown };
    let days = 0;
    if (payload.ok && payload.value) days = estimateDayCount(payload.value);
    const existing = this.rowState.get(row.adapter.id);
    this.rowState.set(row.adapter.id, {
      status: await row.adapter.getStatus(),
      days: (existing?.days ?? 0) + days,
      expanded: existing?.expanded ?? false,
      liveBpm: existing?.liveBpm ?? null,
    });
    this.render();
    window.dispatchEvent(new CustomEvent('aramgah:connector-imported', { detail: { id: row.adapter.id, days } }));
  }

  private async onDisconnect(row: ConnectorRow): Promise<void> {
    if (row.accepts === 'ble') {
      this.bleUnsubscribe?.();
      this.bleUnsubscribe = null;
    }
    withdraw(row.consentScope as Parameters<typeof withdraw>[0]);
    await row.adapter.disconnect();
    const existing = this.rowState.get(row.adapter.id);
    this.rowState.set(row.adapter.id, {
      status: await row.adapter.getStatus(),
      days: existing?.days ?? 0,
      expanded: existing?.expanded ?? false,
      liveBpm: null,
    });
    this.render();
  }

  private toggleDrawer(row: ConnectorRow): void {
    const state = this.rowState.get(row.adapter.id);
    this.rowState.set(row.adapter.id, {
      status: state?.status ?? null,
      days: state?.days ?? 0,
      expanded: !(state?.expanded ?? false),
      liveBpm: state?.liveBpm ?? null,
    });
    this.render();
  }

  private async onCsv(row: ConnectorRow, e: Event): Promise<void> {
    if (row.adapter.id !== 'garmin-stub') return;
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file) return;
    const text = await file.text();
    const ad = row.adapter as GarminStubConnector;
    const r = await ad.ingestCsv(text);
    if (r.ok) {
      const existing = this.rowState.get(row.adapter.id);
      this.rowState.set(row.adapter.id, {
        status: await row.adapter.getStatus(),
        days: (existing?.days ?? 0) + r.value.rows.length,
        expanded: existing?.expanded ?? true,
        liveBpm: existing?.liveBpm ?? null,
      });
      this.render();
      window.dispatchEvent(new CustomEvent('aramgah:connector-imported', { detail: { id: row.adapter.id, days: r.value.rows.length } }));
    }
  }

  private refreshCounts(): void {
    const connected = Array.from(this.rowState.values()).filter((r) => r.status?.connected).length;
    const days = Array.from(this.rowState.values()).reduce((acc, r) => acc + r.days, 0);
    const cEl = this.shadowRoot!.getElementById('connected-count');
    const dEl = this.shadowRoot!.getElementById('days-count');
    if (cEl) cEl.textContent = String(connected);
    if (dEl) dEl.textContent = String(days);
  }
}

if (!customElements.get('aramgah-connect-section')) {
  customElements.define('aramgah-connect-section', AramgahConnectSection);
}

function estimateDayCount(value: unknown): number {
  if (!value || typeof value !== 'object') return 0;
  const v = value as { daysTrailing30?: readonly unknown[]; rows?: readonly unknown[]; recentTicks?: readonly unknown[] };
  return v.daysTrailing30?.length ?? v.rows?.length ?? (v.recentTicks ? 1 : 0);
}
